import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { toast } from "sonner";
import { useConfirm } from "../../components/useConfirm";

const emptyTerms = { finder_fee_percent: "", mgmt_fee_discount_percent: "", carry_discount_percent: "", min_commitment_usd: "", priority_allocation: false, note: "" };

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function TermsFields({ t, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <input className="input-solera fig" type="number" step="0.1" placeholder="Finders fee %" value={t.finder_fee_percent ?? ""} onChange={(e) => onChange({ ...t, finder_fee_percent: e.target.value })} data-testid="group-finder-fee-input" />
      <input className="input-solera fig" type="number" placeholder="Min commitment USD" value={t.min_commitment_usd ?? ""} onChange={(e) => onChange({ ...t, min_commitment_usd: e.target.value })} data-testid="group-min-input" />
      <input className="input-solera fig" type="number" step="0.1" placeholder="Mgmt fee discount %" value={t.mgmt_fee_discount_percent ?? ""} onChange={(e) => onChange({ ...t, mgmt_fee_discount_percent: e.target.value })} data-testid="group-mgmt-input" />
      <input className="input-solera fig" type="number" step="0.1" placeholder="Carry discount %" value={t.carry_discount_percent ?? ""} onChange={(e) => onChange({ ...t, carry_discount_percent: e.target.value })} data-testid="group-carry-input" />
      <label className="flex items-center gap-2 col-span-2 cursor-pointer">
        <input type="checkbox" checked={!!t.priority_allocation} onChange={(e) => onChange({ ...t, priority_allocation: e.target.checked })} className="w-4 h-4" data-testid="group-priority-toggle" />
        <span className="t-small text-bone">Priority allocation</span>
      </label>
      <textarea className="input-solera col-span-2" rows={2} placeholder="Negotiated terms note (shown to members)" value={t.note ?? ""} onChange={(e) => onChange({ ...t, note: e.target.value })} data-testid="group-note-input" />
    </div>
  );
}

export default function AdminGroups() {
  const [groups, setGroups] = useState([]);
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", terms: { ...emptyTerms }, active: true });
  const [emails, setEmails] = useState("");
  const [pullUrl, setPullUrl] = useState("");
  const [pullEnabled, setPullEnabled] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useConfirm();

  const loadGroups = () => api.get("/admin/groups").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => { loadGroups(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const termsPayload = (t) => ({
    finder_fee_percent: num(t.finder_fee_percent), mgmt_fee_discount_percent: num(t.mgmt_fee_discount_percent),
    carry_discount_percent: num(t.carry_discount_percent), min_commitment_usd: num(t.min_commitment_usd),
    priority_allocation: !!t.priority_allocation, note: t.note || null,
  });

  const openGroup = async (g) => {
    setNewKey("");
    try {
      const { data } = await api.get(`/admin/groups/${g.group_id}`);
      setSel(data);
      setPullUrl(data.pull?.url || "");
      setPullEnabled(!!data.pull?.enabled);
    } catch (e) { toast.error(apiError(e)); }
  };

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/groups", { name: form.name, description: form.description, terms: termsPayload(form.terms), active: form.active });
      toast.success("Group created.");
      setCreating(false);
      setForm({ name: "", description: "", terms: { ...emptyTerms }, active: true });
      loadGroups();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveGroup = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/groups/${sel.group_id}`, { name: sel.name, description: sel.description || "", terms: termsPayload(sel.terms || {}), active: sel.active });
      toast.success("Group saved.");
      loadGroups();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const delGroup = async (g) => {
    if (!(await confirm({ title: "Delete group", message: `Delete ${g.name}? Its roster will be removed. This cannot be undone.`, confirmLabel: "Delete group", danger: true }))) return;
    try { await api.delete(`/admin/groups/${g.group_id}`); toast.success("Group deleted."); setSel(null); loadGroups(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addMembers = async () => {
    const list = emails.split(/[\s,;]+/).filter((x) => x.includes("@"));
    if (!list.length) return;
    try { const { data } = await api.post(`/admin/groups/${sel.group_id}/members`, { emails: list }); toast.success(`Added ${data.added}.`); setEmails(""); setSel({ ...sel, members: data.members }); loadGroups(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const removeMember = async (email) => {
    if (!(await confirm({ title: "Remove member", message: `Remove ${email} from ${sel?.name || "this group"}? They keep their account but lose the group's negotiated terms.`, confirmLabel: "Remove member", danger: true }))) return;
    try { const { data } = await api.post(`/admin/groups/${sel.group_id}/members/remove`, { emails: [email] }); setSel({ ...sel, members: data.members }); loadGroups(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const rotateKey = async () => {
    if (sel.api_key_prefix && !(await confirm({ title: "Rotate API key", message: `Rotate the API key for ${sel.name}? The current key stops working immediately and any partner integration using it will break until it is updated with the new key.`, confirmLabel: "Rotate key", danger: true }))) return;
    try { const { data } = await api.post(`/admin/groups/${sel.group_id}/rotate-key`); setNewKey(data.api_key); setSel({ ...sel, api_key_prefix: data.api_key_prefix }); toast.success("API key generated. Copy it now."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const savePull = async () => {
    try { await api.put(`/admin/groups/${sel.group_id}/pull`, { url: pullUrl, enabled: pullEnabled }); toast.success("Sync settings saved."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const syncNow = async () => {
    try { const { data } = await api.post(`/admin/groups/${sel.group_id}/sync`); toast.success(`Synced. Added ${data.added}, removed ${data.removed}, total ${data.total}.`); openGroup(sel); loadGroups(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-groups">
      <div className="flex items-center justify-between gap-6 flex-wrap">
        <div>
          <span className="t-label">Investor groups</span>
          <p className="t-cap text-muted-s mt-2 measure">Tag investors to a partner organisation with negotiated terms. Partners can sync their roster by API key or a shared source URL.</p>
        </div>
        <button className="btn-ghost" onClick={() => setCreating((c) => !c)} data-testid="new-group-button">{creating ? "Cancel" : "New group"}</button>
      </div>

      {creating && (
        <div className="mt-6 p-6 space-y-3 max-w-xl" style={{ background: "#14171A", border: "1px solid #24272B" }} data-testid="group-create-form">
          <input className="input-solera" placeholder="Group name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name-input" />
          <input className="input-solera" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="group-description-input" />
          <TermsFields t={form.terms} onChange={(t) => setForm({ ...form, terms: t })} />
          <button className="btn-primary" disabled={busy || !form.name} onClick={create} data-testid="create-group-button">Create group</button>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-[300px_1fr] gap-10">
        <div className="space-y-0">
          {groups.map((g) => (
            <button key={g.group_id} onClick={() => openGroup(g)} className={`w-full text-left hairline py-4 ${sel?.group_id === g.group_id ? "!text-[#E8E6E1]" : ""}`} data-testid={`group-row-${g.group_id}`}>
              <div className="flex justify-between gap-4">
                <span className="t-small text-bone">{g.name}</span>
                <span className="t-cap text-muted-s fig">{g.member_count}</span>
              </div>
              <span className="t-cap text-muted-s">{g.active ? "Active" : "Inactive"}</span>
            </button>
          ))}
          {groups.length === 0 && <p className="t-small text-muted-s">No groups yet.</p>}
        </div>

        {sel && (
          <div className="space-y-8" data-testid="group-detail">
            <div className="space-y-3 max-w-xl">
              <div className="flex justify-between items-center gap-4">
                <span className="t-label">Group and terms</span>
                <button className="t-label hover:!text-[#E8E6E1]" onClick={() => delGroup(sel)} data-testid="delete-group-button">Delete</button>
              </div>
              <input className="input-solera" value={sel.name} onChange={(e) => setSel({ ...sel, name: e.target.value })} data-testid="edit-group-name" />
              <input className="input-solera" value={sel.description || ""} onChange={(e) => setSel({ ...sel, description: e.target.value })} />
              <TermsFields t={sel.terms || {}} onChange={(t) => setSel({ ...sel, terms: t })} />
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!sel.active} onChange={(e) => setSel({ ...sel, active: e.target.checked })} className="w-4 h-4" data-testid="group-active-toggle" />
                <span className="t-small text-bone">Active</span>
              </label>
              <button className="btn-primary" disabled={busy} onClick={saveGroup} data-testid="save-group-button">Save group</button>
            </div>

            <div className="space-y-3 max-w-xl">
              <span className="t-label">Roster ({sel.members?.length || 0})</span>
              <div className="flex gap-3">
                <input className="input-solera" placeholder="Add emails, comma or space separated" value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="group-emails-input" />
                <button className="btn-ghost whitespace-nowrap" onClick={addMembers} data-testid="add-members-button">Add</button>
              </div>
              <div className="space-y-0">
                {(sel.members || []).map((em) => (
                  <div key={em} className="hairline py-2 flex justify-between gap-4" data-testid={`roster-${em}`}>
                    <span className="t-small text-muted-s">{em}</span>
                    <button onClick={() => removeMember(em)} className="t-cap text-muted-s hover:!text-[#E8E6E1]" data-testid={`remove-member-${em}`}>Remove</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 max-w-xl">
              <span className="t-label">Partner API key (push)</span>
              <p className="t-cap text-muted-s">The partner uses this key to add or remove members via the API. Rotating replaces any previous key.</p>
              {newKey ? (
                <div className="p-3 fig t-small text-bone break-all" style={{ background: "#0E1012", border: "1px solid #24272B" }} data-testid="new-api-key">{newKey}</div>
              ) : (
                <p className="t-cap text-muted-s fig">{sel.api_key_prefix ? `${sel.api_key_prefix} (set)` : "No key issued"}</p>
              )}
              <button className="btn-ghost" onClick={rotateKey} data-testid="rotate-key-button">{sel.api_key_prefix ? "Rotate key" : "Generate key"}</button>
            </div>

            <div className="space-y-3 max-w-xl">
              <span className="t-label">Shared source sync (pull)</span>
              <p className="t-cap text-muted-s">Solera fetches this URL (a JSON list, an object with an emails array, or CSV) and replaces the roster. Runs daily when enabled.</p>
              <input className="input-solera" placeholder="https://partner.example.com/members.json" value={pullUrl} onChange={(e) => setPullUrl(e.target.value)} data-testid="pull-url-input" />
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={pullEnabled} onChange={(e) => setPullEnabled(e.target.checked)} className="w-4 h-4" data-testid="pull-enabled-toggle" />
                <span className="t-small text-bone">Enable daily sync</span>
              </label>
              <div className="flex gap-3">
                <button className="btn-ghost" onClick={savePull} data-testid="save-pull-button">Save</button>
                <button className="btn-ghost" onClick={syncNow} data-testid="sync-now-button">Sync now</button>
              </div>
              {sel.pull?.last_synced_at && <p className="t-cap text-muted-s fig">Last synced {sel.pull.last_synced_at.slice(0, 16).replace("T", " ")} UTC</p>}
            </div>
          </div>
        )}
      </div>
      {dialog}
    </div>
  );
}
